import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { DESTINATION_OPTIONS } from "@/data/site";

const baseField =
  "w-full bg-transparent border-b py-3 text-base outline-none focus:border-terra transition-colors";

export function EnquiryForm({ preset = {}, idPrefix = "callback", dark = false, onDone }) {
  const empty = { name: "", phone: "", email: "", destination: preset.destination || "", travel_month: "", travellers: "", message: "" };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/enquiries", {
        ...form,
        email: form.email || null,
        travellers: form.travellers ? Number(form.travellers) : null,
        trip_slug: preset.trip_slug || "",
        trip_title: preset.trip_title || "",
      });
      toast.success("Got it! Our team will call you shortly.");
      setForm(empty);
      onDone?.();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const opt = dark ? "bg-ink text-cream" : "bg-paper text-ink";
  const field = `${baseField} ${dark ? "border-cream/30 placeholder:text-cream/60" : "border-ink/20 placeholder:text-stone-600"}`;
  return (
    <form onSubmit={submit} data-testid={`${idPrefix}-form`} className={`grid sm:grid-cols-2 gap-x-6 gap-y-2 ${dark ? "text-cream" : "text-ink"}`}>
      <input required minLength={2} placeholder="Your name *" value={form.name} onChange={set("name")} className={field} data-testid={`${idPrefix}-name-input`} />
      <input required type="tel" minLength={7} placeholder="Phone number *" value={form.phone} onChange={set("phone")} className={field} data-testid={`${idPrefix}-phone-input`} />
      <input type="email" placeholder="Email (optional)" value={form.email} onChange={set("email")} className={field} data-testid={`${idPrefix}-email-input`} />
      <select value={form.destination} onChange={set("destination")} className={`${field} cursor-pointer`} data-testid={`${idPrefix}-destination-select`}>
        <option className={opt} value="">Destination</option>
        {DESTINATION_OPTIONS.map((d) => <option className={opt} key={d} value={d}>{d}</option>)}
      </select>
      <input placeholder="Travel month" value={form.travel_month} onChange={set("travel_month")} className={field} data-testid={`${idPrefix}-month-input`} />
      <input type="number" min={1} placeholder="No. of travellers" value={form.travellers} onChange={set("travellers")} className={field} data-testid={`${idPrefix}-travellers-input`} />
      <textarea rows={2} placeholder="Anything we should know?" value={form.message} onChange={set("message")} className={`${field} sm:col-span-2 resize-none`} data-testid={`${idPrefix}-message-input`} />
      <button type="submit" disabled={busy} data-testid={`${idPrefix}-submit-btn`}
        className="group sm:col-span-2 mt-5 inline-flex items-center justify-between gap-3 rounded-full bg-terra text-paper pl-7 pr-2 py-2 font-display font-bold text-lg transition-[background-color,transform] duration-300 hover:bg-[#a5370a] active:scale-[0.98] disabled:opacity-60">
        {busy ? "Sending…" : "Request a callback"}
        <span className="grid place-items-center h-11 w-11 rounded-full bg-paper text-terra transition-transform duration-300 group-hover:rotate-45">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowUpRight className="h-5 w-5" />}
        </span>
      </button>
    </form>
  );
}
